"use client"

import { colorPartsOf } from "@v2/lib/colorParts";
import { ProductDetailType } from "@/app/types/product-site"
import { Box, Button, colors, Divider, Fade, FormControlLabel, IconButton, Modal, NoSsr, Skeleton, styled, Switch, TextField, Typography, useTheme } from "@mui/material"
import MediaV2 from "@v2/components/MediaV2"
import FlexBox from "@v2/components/styled/FlexBox"
import api from "@v2/lib/api"
import axios, { AxiosError } from "@v2/lib/axios"
import { useAppSelector } from "@v2/lib/redux/hooks"
import { MessageSquarePlusIcon, MessageSquareShare, X } from "lucide-react"
import Link from "next/link"
import { usePathname, useSearchParams } from "next/navigation"
import { useSnackbar } from "notistack"
import { ChangeEvent, Fragment, useCallback, useState } from "react"

export default function PdpAddCommentButton({
    product,
    siteProductId
}: {
    product: ProductDetailType,
    siteProductId: number
}) {

    const session = useAppSelector(s => s.general.session)
    const isAuth = session?.isAuthenticated ?? false;

    const pathname = usePathname()
    const searchParams = useSearchParams()
    const currentPath = pathname + (searchParams.size > 0 ? `?${searchParams.toString()}` : '')
    const signInHref = `/fa/sign-in?callback_url=${encodeURIComponent(`${currentPath}#comments`)}`

    const { enqueueSnackbar } = useSnackbar()


    const [modalIsOpen, setModalIsOpen] = useState(false)

    const onClose = useCallback(() => {
        setModalIsOpen(false)
        setCommentText(null)
        setIsUnknown(true)
    }, [])

    const onOpen = useCallback(() => {

        if (!isAuth) return enqueueSnackbar({
            message: (
                <Box>
                    <Typography sx={{ fontSize: 14 }}>برای ثبت دیدگاه ابتدا وارد حساب کاربری شود</Typography>
                    <Button color="inherit" variant="outlined" component={Link} prefetch={false} href={signInHref} sx={{
                        mt: 1, minWidth: 68
                    }}>ورود</Button>
                </Box>
            )
        })
        setModalIsOpen(true)
    }, [isAuth, signInHref])

    const [commentText, setCommentText] = useState<string | null>(null)
    const onChangeTextField = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
        setCommentText(event.target.value || null)
    }, [])


    const [isUnknown, setIsUnknown] = useState(false)
    const onChangeUnknownUser = useCallback((e, value: boolean) => {
        setIsUnknown(value)
    }, [])

    const tags: string[] = []
    tags.push(product.size)

    colorPartsOf(product).forEach(part => tags.push(`${part.title} ${part.colors.map(i => i.title).join(' , ')}`))
    const [submitInProcess, setSubmitInProcess] = useState(false)
    const submitComment = useCallback(() => {

        const dataToSend = {
            comment_id: null,
            title: null,
            description: commentText,
            rate: null,
            is_unknown: isUnknown,
            tags

        }
        setSubmitInProcess(true)
        axios.post(api.comments(siteProductId), dataToSend).then(() => {
            setSubmitInProcess(false)

            onClose()

            enqueueSnackbar("دیدگاه شما ثبت شد و پس از بررسی منتشر خواهد شد", {
                autoHideDuration: 8000,
                variant: 'success',
            })
        }).catch((error: AxiosError) => {
            enqueueSnackbar(error.response?.data?.message ?? 'خطا در ارسال دیدگاه', {
                autoHideDuration: 8000,
                variant: 'error',
            })
            setSubmitInProcess(false)
        })


    }, [tags, isUnknown, commentText, product, siteProductId])


    const theme = useTheme()



    const image = product.images.find(i => i.default) ?? product.images[0]
    return (
        <NoSsr fallback={
            <Skeleton variant="rounded" height={48} width={114} />
        }>
            <Modal
                open={modalIsOpen}
                onClose={onClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={{
                    bgcolor: '#fff',
                    maxWidth: 560,
                    margin: 'auto',
                    marginTop: 6,
                    borderRadius: 1,
                    [theme.breakpoints.down('md')]: {
                        position: 'absolute',
                        right: 0,
                        left: 0,
                        maxWidth: 'unset',
                        bottom: 0
                    }
                }}>

                    <Box sx={{
                        position: 'relative',
                        borderBottom: '1px solid',
                        borderColor: colors.grey[300],
                        height: 56,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Typography sx={{
                            fontSize: 18,
                            fontWeight: 400
                        }}>
                            ثبت دیدگاه
                        </Typography>

                        <IconButton sx={{
                            height: 32, width: 32,
                            position: 'absolute',
                            left: 16
                        }} onClick={onClose}>
                            <X />
                        </IconButton>
                    </Box>

                    <Box sx={{
                        p: 2
                    }}>
                        <Title>
                            مشخصات محصول
                        </Title>


                        <FlexBox sx={{
                            mt: 1,
                            [theme.breakpoints.down('md')]: {
                                flexDirection: 'column',
                                alignItems: 'flex-start'
                            }
                        }}>
                            <MediaV2
                                media_id={image?.id}
                                withLoading
                                boxProps={{
                                    sx: {
                                        width: 160,
                                        aspectRatio: '16 / 9'
                                    }
                                }}
                                alt={image?.alt ?? product.name}
                                title={image?.title}
                                imageProps={{
                                    fill: true,
                                }}
                                width={160}
                            />

                            <Box sx={{
                                ml: 2,
                            }}>
                                <Typography className="en-num">{product.name}</Typography>
                                <FlexBox sx={{
                                    bgcolor: colors.grey[100],
                                    py: 0.5,
                                    px: 1,
                                    borderRadius: 0.5,
                                    mt: 1,
                                    '& span': {
                                        fontSize: 12,
                                        color: colors.grey[800]
                                    },
                                    '& strong': {
                                        display: 'inline-block',
                                        mr: 0.5,
                                        fontWeight: 400,
                                        color: colors.grey[900],
                                    }
                                }}>

                                    <Typography className="en-num" component={'span'} fontWeight={500}>{product.size}</Typography>
                                    <Divider flexItem orientation="vertical" sx={{ my: 0.5, mx: 1 }} />

                                    {colorPartsOf(product).map((part, index, parts) => (
                                        <Fragment key={part.slug}>
                                            <Typography className="en-num" component={'span'}>
                                                <strong>{part.title}</strong>
                                                {part.colors.map(i => i.title).join(' , ')}
                                            </Typography>
                                            {index < parts.length - 1 && <Divider flexItem orientation="vertical" sx={{ my: 0.5, mx: 1 }} />}
                                        </Fragment>
                                    ))}

                                </FlexBox>
                            </Box>
                        </FlexBox>
                        <Divider sx={{
                            mt: 2
                        }} />
                        <Box sx={{
                            mt: 2
                        }}>
                            <FlexBox sx={{
                                justifyContent: 'space-between',
                                minHeight: 26
                            }}>
                                <Title>دیدگاه شما درباره محصول</Title>
                                {(!!commentText) && (
                                    (commentText.length < 150) ? (
                                        <Fade in={true}>
                                            <Typography sx={{
                                                fontSize: 11,
                                                py: 0.5,
                                                px: 1,
                                                bgcolor: colors.green[100],
                                                color: colors.green[700],
                                                borderRadius: 1
                                            }}>
                                                پیشنهاد میکنم بیشتر توضیح بدید
                                            </Typography>
                                        </Fade>
                                    ) : (
                                        <Fade in={true}>
                                            <Typography sx={{
                                                fontSize: 12,
                                                py: 0.5,
                                                px: 1,
                                                bgcolor: colors.blue[100],
                                                color: colors.blue[700],
                                                borderRadius: 1.5
                                            }}>
                                                شما بی‌نظیری
                                            </Typography>
                                        </Fade>
                                    )
                                )}
                            </FlexBox>
                            <TextField onChange={onChangeTextField} autoFocus multiline rows={4} fullWidth placeholder="دیدگاه خود را بنویسید ..." sx={{
                                mt: 2
                            }} />
                        </Box>
                        {/* <Divider sx={{
                            mt: 2
                        }} /> */}
                        <Box sx={{
                            mt: 2
                        }}>

                            <FormControlLabel sx={{
                                flexDirection: 'row-reverse',
                                width: '100%',
                                justifyContent: 'space-between',
                                m: 0,
                                userSelect: 'none',
                                '& .MuiFormControlLabel-label': {
                                    fontSize: 13
                                }
                            }} onChange={onChangeUnknownUser} control={<Switch />} label="ارسال دیدگاه به صورت ناشناس" />
                        </Box>

                        <Button fullWidth variant="contained" color="primary" disableElevation
                            disabled={!commentText || commentText.length < 10}
                            onClick={submitComment}
                            endIcon={!submitInProcess ? <MessageSquareShare color={'#fff'} /> : undefined}
                            loading={submitInProcess}
                            sx={{
                                height: 48,
                                mt: 3
                            }}>
                            ارسال دیدگاه

                        </Button>

                        <Typography sx={{
                            fontSize: 11,
                            mt: 1,
                            textAlign: 'center',
                            '& > a': {
                                textDecoration: 'underline',
                                color: colors.red[900]
                            }
                        }}>
                            ارسال دیدگاه به معنی موافقت با <a href="/fa/pages/terms-and-conditions" target="_blank">قوانین و مقررات عینک وحدت</a> است.
                        </Typography>
                    </Box>
                </Box>
            </Modal>
            <Fade in={true}>
                <Button color="primary" variant="contained" disableElevation onClick={onOpen} sx={{
                    height: 36,
                    minWidth: 114,
                    borderRadius: 0.8
                }} startIcon={<MessageSquarePlusIcon size={18} />} >افزودن دیدگاه</Button>
            </Fade>
        </NoSsr>
    )
}

const Title = styled(Typography)({
    fontWeight: 500
})
